import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ClockIcon,
  GlobeIcon,
  HelpCircleIcon,
  MonitorIcon,
  ShieldIcon,
  SmartphoneIcon,
} from "lucide-react";
import { useEffect, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  fetchAuthSessions,
  revokeAllOtherAuthSessions,
  revokeAuthSession,
  type AuthSession,
} from "@/lib/auth/session-api";

interface SessionManagementDialogProps {
  open: boolean;
  onClose: () => void;
}

type DeviceInfo = {
  deviceName: string;
  operatingSystem: "macOs" | "windows" | "android" | "ios" | "unknown";
  Icon: ComponentType<{ className?: string }>;
};

const SESSION_QUERY_KEY = ["auth", "sessions"];

function getDeviceInfo(session: AuthSession): DeviceInfo {
  const userAgent = (session.userAgent ?? "").toLowerCase();
  if (userAgent.includes("android")) {
    return { deviceName: "android", operatingSystem: "android", Icon: SmartphoneIcon };
  }
  if (
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("ios")
  ) {
    return { deviceName: "ios", operatingSystem: "ios", Icon: SmartphoneIcon };
  }
  if (userAgent.includes("mac")) {
    return { deviceName: "macOs", operatingSystem: "macOs", Icon: MonitorIcon };
  }
  if (userAgent.includes("windows")) {
    return { deviceName: "windows", operatingSystem: "windows", Icon: MonitorIcon };
  }
  return { deviceName: "unknown", operatingSystem: "unknown", Icon: HelpCircleIcon };
}

function formatTimeAgo(timestamp: string, locale: string, unknownLabel: string): string {
  const elapsedSeconds = Math.round((Date.now() - Date.parse(timestamp)) / 1000);
  if (!Number.isFinite(elapsedSeconds)) return unknownLabel;
  const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["week", 604_800],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  for (const [unit, seconds] of units) {
    if (Math.abs(elapsedSeconds) >= seconds) {
      return formatter.format(-Math.round(elapsedSeconds / seconds), unit);
    }
  }
  return formatter.format(0, "second");
}

export function SessionManagementDialog({ open, onClose }: SessionManagementDialogProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const sessionsQuery = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: ({ signal }) => fetchAuthSessions(signal),
    enabled: open,
    staleTime: 0,
  });
  const revokeMutation = useMutation({
    mutationFn: revokeAuthSession,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
  });
  const revokeOthersMutation = useMutation({
    mutationFn: revokeAllOtherAuthSessions,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
  });

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        !revokeMutation.isPending &&
        !revokeOthersMutation.isPending
      ) {
        onClose();
      }
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [onClose, open, revokeMutation.isPending, revokeOthersMutation.isPending]);

  if (!open) return null;
  const sessions = sessionsQuery.data ?? [];
  const activeCount = sessions.length;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-management-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-overlay/80 p-4"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col gap-4 rounded-lg border border-border bg-bg-elevated p-6 shadow-lg">
        <div className="flex items-start gap-4">
          <div className="flex flex-col gap-1">
            <h2
              id="session-management-title"
              className="flex items-center gap-2 text-base font-semibold text-text-primary"
            >
              <ShieldIcon aria-hidden="true" className="h-5 w-5" />
              {t("common:sessionManagement.title")}
            </h2>
            <p className="text-sm text-text-muted">
              {t("common:sessionManagement.description")}
            </p>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pr-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-text-muted">
              {t("common:sessionManagement.activeCount", { count: activeCount })}
            </p>
            <Button
              type="button"
              variant="danger"
              size="sm"
              className="shrink-0"
              disabled={revokeOthersMutation.isPending || activeCount <= 1}
              onClick={() => revokeOthersMutation.mutate()}
            >
              {revokeOthersMutation.isPending
                ? t("common:sessionManagement.revoking")
                : t("common:sessionManagement.signOutOthers")}
            </Button>
          </div>
          {sessionsQuery.isLoading ? (
            <SessionListSkeleton label={t("common:loading")} />
          ) : null}
          {!sessionsQuery.isLoading && sessionsQuery.isError ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-text-muted">
              {t("common:sessionManagement.loadError")}
            </div>
          ) : null}
          {!sessionsQuery.isLoading && !sessionsQuery.isError && sessions.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-text-muted">
              {t("common:sessionManagement.empty")}
            </div>
          ) : null}
          {!sessionsQuery.isLoading && !sessionsQuery.isError && sessions.length > 0 ? (
            <div className="flex flex-col gap-2">
              {sessions.map((session) => (
                <SessionRow
                  key={session.id}
                  session={session}
                  locale={i18n.language}
                  isRevoking={
                    revokeMutation.isPending && revokeMutation.variables === session.id
                  }
                  onRevoke={() => revokeMutation.mutate(session.id)}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SessionListSkeleton({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-3" aria-label={label}>
      {[0, 1, 2].map((item) => (
        <div key={item} className="h-16 animate-pulse rounded-lg bg-bg-surface" />
      ))}
    </div>
  );
}

interface SessionRowProps {
  session: AuthSession;
  locale: string;
  isRevoking: boolean;
  onRevoke: () => void;
}

function SessionRow({ session, locale, isRevoking, onRevoke }: SessionRowProps) {
  const { t } = useTranslation();
  const device = getDeviceInfo(session);
  const deviceName =
    session.deviceName ?? t(`common:sessionManagement.devices.${device.deviceName}`);
  return (
    <div
      className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
        session.current
          ? "border-action-primary/30 bg-action-primary/5"
          : "hover:bg-bg-surface"
      }`}
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          session.current
            ? "bg-action-primary/10 text-action-primary"
            : "bg-bg-surface text-text-muted"
        }`}
      >
        <device.Icon aria-hidden="true" className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-text-primary">{deviceName}</p>
          {session.current ? (
            <span className="rounded-md bg-action-secondary px-1.5 py-0 text-xs font-medium text-text-secondary">
              {t("common:sessionManagement.thisDevice")}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-muted">
          {session.ipAddress ? (
            <>
              <GlobeIcon aria-hidden="true" className="h-3 w-3 shrink-0" />
              <span>{session.ipAddress}</span>
              <span className="text-text-muted/50">·</span>
            </>
          ) : null}
          <ClockIcon aria-hidden="true" className="h-3 w-3 shrink-0" />
          <span>
            {formatTimeAgo(
              session.lastActivityAt,
              locale,
              t("common:sessionManagement.unknown"),
            )}
          </span>
        </div>
      </div>
      <div className="shrink-0">
        {session.current ? (
          <span className="inline-flex items-center gap-1 rounded-md bg-status-pass/10 px-2 py-1 text-xs font-medium text-status-pass">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-status-pass" />
            {t("common:sessionManagement.active")}
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-action-danger hover:bg-action-danger/10 hover:text-action-danger"
            disabled={isRevoking}
            onClick={onRevoke}
          >
            {isRevoking
              ? t("common:sessionManagement.revoking")
              : t("common:sessionManagement.revoke")}
          </Button>
        )}
      </div>
    </div>
  );
}
